import { useEffect, useRef, useState, type MutableRefObject } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ApiError } from "@/lib/api-client";
import { Button, Skeleton } from "@nexus/ui";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { usePermissions } from "@/features/auth/hooks/usePermissions";
import {
  postFlightOpportunitiesQueryOptions,
  usePostFlightDoubleDown,
} from "@/lib/queries";
import { AdCard } from "@/features/post-flight/components/AdCard";
import { PostFlightFilters } from "@/features/post-flight/components/PostFlightFilters";
import { AdDrawer } from "@/features/post-flight/components/AdDrawer";
import { DoubleDownDialog } from "@/features/post-flight/components/DoubleDownDialog";
import {
  adsMatchingFilters,
  creativeKind,
  openAdHash,
  resolveQualifyingListWindowRangeLabel,
  qualifyingAds,
  type PostFlightAdType,
  type PostFlightWindow,
} from "@/features/post-flight/lib/post-flight-list";
import {
  postFlightDoubleDownFailureMessage,
  type PostFlightDoubleDownPostFailureKind,
} from "@/features/post-flight/lib/post-flight-double-down-errors";
import type { StartPostFlightDoubleDownRequest } from "@/features/post-flight/api/post-flight.api";

interface PostFlightPageProps {
  windowDays: PostFlightWindow;
  adHash?: string;
  onWindowChange: (window: PostFlightWindow) => void;
  onAdSelect?: (hash: string) => void;
  onAdClose?: () => void;
}

export function PostFlightPage({
  windowDays,
  adHash,
  onWindowChange,
  onAdSelect,
  onAdClose,
}: PostFlightPageProps) {
  const { t } = useTranslation();
  const tenantId = useAuth((state) => state.activeTenantId);
  const perms = usePermissions();
  // The double-down route rejects without an explicit grant on this organization.
  const canDoubleDown = perms.POST_FLIGHT_DOUBLE_DOWN?.isOn === true;
  const query = useQuery(postFlightOpportunitiesQueryOptions(tenantId));
  const [types, setTypes] = useState<string[]>([]);
  const [adTypes, setAdTypes] = useState<PostFlightAdType[]>(["video"]);
  const [platform, setPlatform] = useState("");
  const [doubleDownHash, setDoubleDownHash] = useState<string | null>(null);
  const [returnToDrawerHash, setReturnToDrawerHash] = useState<string | null>(null);
  const [shouldPlayVideo, setShouldPlayVideo] = useState(false);
  const [postFailure, setPostFailure] = useState<{
    kind: PostFlightDoubleDownPostFailureKind;
    error: ApiError;
  } | null>(null);
  const inFlightKeysRef = useRef(new Map<string, string>());
  const pollTokensRef = useRef(new WeakMap<StartPostFlightDoubleDownRequest, number>());
  const latestPollTokenRef = useRef(new Map<string, number>());
  const pollTokenSeqRef = useRef(0);
  const doubleDown = usePostFlightDoubleDown({
    onAccepted: (variables) => {
      retainInFlightDoubleDown(variables, {
        inFlightKeysRef,
        pollTokensRef,
        latestPollTokenRef,
        pollTokenSeqRef,
      });
      setPostFailure(null);
      setDoubleDownHash(null);
      setReturnToDrawerHash(null);
    },
    onPollFinished: (variables) => {
      releaseInFlightDoubleDown(variables, {
        inFlightKeysRef,
        pollTokensRef,
        latestPollTokenRef,
        pollTokenSeqRef,
      });
    },
    onPostFailure: ({ kind, error }) => {
      if (kind === "ad-gone") {
        toast.error(postFlightDoubleDownFailureMessage(t, kind, error));
        setPostFailure(null);
        setDoubleDownHash(null);
        setReturnToDrawerHash(null);
        return;
      }
      if (kind === "toast") {
        return;
      }
      setPostFailure({ kind, error });
    },
  });
  const previousTenantId = useRef(tenantId);
  const expandedForTenantAd = useRef<string | null>(null);
  const doubleDownIdempotencyKeyRef = useRef<string>(crypto.randomUUID());

  useEffect(() => {
    if (!doubleDownHash) return;
    // A dialog open mints a key. Retrying a job that is still in flight reuses
    // that job's key so the server matches the existing claim.
    const inFlightKey = inFlightKeysRef.current.get(doubleDownHash);
    doubleDownIdempotencyKeyRef.current = inFlightKey ?? crypto.randomUUID();
    setPostFailure(null);
  }, [doubleDownHash]);

  useEffect(() => {
    setDoubleDownHash(null);
    setReturnToDrawerHash(null);
  }, [windowDays]);

  useEffect(() => {
    if (previousTenantId.current === tenantId) return;
    previousTenantId.current = tenantId;
    expandedForTenantAd.current = null;
    inFlightKeysRef.current.clear();
    latestPollTokenRef.current.clear();
    setTypes([]);
    setAdTypes(["video"]);
    setPlatform("");
    setDoubleDownHash(null);
    setReturnToDrawerHash(null);
    setShouldPlayVideo(false);
    onAdClose?.();
    if (windowDays !== 90) onWindowChange(90);
  }, [tenantId, windowDays, onAdClose, onWindowChange]);

  const opportunities = query.data?.opportunities ?? [];
  const ads = qualifyingAds(opportunities, windowDays);
  const linkedKind = findLinkedCreativeKind(ads, adHash, query.isSuccess);

  useEffect(() => {
    if (!adHash) {
      expandedForTenantAd.current = null;
      return;
    }
    if (!linkedKind || expandedForTenantAd.current === `${tenantId}:${adHash}`) return;
    expandedForTenantAd.current = `${tenantId}:${adHash}`;
    setAdTypes((current) =>
      current.length === 0 || current.includes(linkedKind) ? current : [...current, linkedKind],
    );
  }, [adHash, linkedKind, tenantId]);

  const visibleAds = adsMatchingFilters(ads, types, platform, adTypes);
  const openHash = openAdHash(visibleAds, adHash, query.isSuccess);
  const drawerAd = visibleAds.find((ad) => ad.hash === openHash) ?? null;
  const doubleDownAd = visibleAds.find((ad) => ad.hash === doubleDownHash) ?? null;
  const doubleDownAdUnavailable =
    Boolean(doubleDownHash) && query.isSuccess && doubleDownAd === null;
  const listAsOf = query.data?.as_of ?? null;
  const range = resolveQualifyingListWindowRangeLabel(ads, listAsOf, windowDays);

  if (!tenantId) {
    return <p>{t("postFlight.error.noTenant", "Choose an organization to see qualifying ads.")}</p>;
  }

  if (query.isPending) {
    return (
      <div role="status" aria-label={t("postFlight.loading", "Loading qualifying ads")}>
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (query.isError && query.error instanceof ApiError && query.error.status === 403) {
    return <h1>{t("errors.noAccess", "You don't have this page yet")}</h1>;
  }

  if (query.isError && query.error instanceof ApiError && query.error.status === 400) {
    return (
      <div role="alert">
        <p>{t("postFlight.error.noClient", "This organization has no connected data client.")}</p>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div role="alert" className="flex flex-col items-start gap-3">
        <p>{t("postFlight.error.load", "Couldn't load qualifying ads. Try again.")}</p>
        <Button type="button" variant="outline" onClick={() => void query.refetch()}>
          {t("common.retry", "Retry")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="border-b border-border">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3 px-6 py-8">
        <PostFlightFilters
          platform={platform}
          onPlatformChange={setPlatform}
          adTypes={adTypes}
          onAdTypesChange={setAdTypes}
          types={types}
          onTypesChange={setTypes}
          windowDays={windowDays}
          onWindowChange={onWindowChange}
          opportunities={opportunities}
          ads={ads}
        />
      </div>
      </div>

      <div className="flex flex-col gap-6 px-6 py-6">
      <p className="text-[13px] text-muted-foreground">
        {t("postFlight.verdict.qualifyingAds", { count: visibleAds.length })}
        {range ? ` · ${range}` : ""}
      </p>
      {ads.length === 0 ? (
        <h2>{t("postFlight.empty.none", "No qualifying ads in the last {{days}} days.", { days: windowDays })}</h2>
      ) : null}

      {ads.length > 0 && visibleAds.length === 0 ? (
        <div>
          <h2>{t("postFlight.empty.filters", "No results match your filters.")}</h2>
          <p>{t("postFlight.empty.filtersHint", "Try another opportunity type or platform.")}</p>
        </div>
      ) : null}

      {visibleAds.length > 0 ? (
        <div className="flex flex-col gap-3">
          <h2 className="text-[22px] leading-tight font-semibold">{t("postFlight.list.title", "Opportunities")}</h2>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,260px),1fr))] items-start gap-4">
            {visibleAds.map((ad) => (
              <AdCard
                key={ad.hash}
                ad={ad}
                listAsOf={listAsOf}
                canDoubleDown={canDoubleDown}
                onOpen={() => {
                  setShouldPlayVideo(false);
                  onAdSelect?.(ad.hash);
                }}
                onPlay={() => {
                  setShouldPlayVideo(true);
                  onAdSelect?.(ad.hash);
                }}
                onDoubleDown={() => {
                  setReturnToDrawerHash(null);
                  setDoubleDownHash(ad.hash);
                }}
              />
            ))}
          </div>
        </div>
      ) : null}

      <AdDrawer
        ad={drawerAd}
        windowDays={windowDays}
        listAsOf={listAsOf}
        canDoubleDown={canDoubleDown}
        shouldPlayVideo={shouldPlayVideo}
        onVideoStarted={() => setShouldPlayVideo(false)}
        onClose={() => {
          setShouldPlayVideo(false);
          onAdClose?.();
        }}
        onDoubleDown={() => {
          if (!drawerAd) return;
          setReturnToDrawerHash(drawerAd.hash);
          setDoubleDownHash(drawerAd.hash);
          onAdClose?.();
        }}
      />
      <DoubleDownDialog
        open={doubleDownHash !== null}
        ad={doubleDownAd}
        adUnavailable={doubleDownAdUnavailable}
        postFailure={
          postFailure && postFailure.kind !== "ad-gone" && postFailure.kind !== "toast"
            ? {
                kind: postFailure.kind,
                message: postFlightDoubleDownFailureMessage(t, postFailure.kind, postFailure.error),
              }
            : null
        }
        isReloadingList={query.isFetching}
        onReloadList={() => {
          void query.refetch().then((result) => {
            if (result.isSuccess) setPostFailure(null);
          });
        }}
        isGenerating={doubleDown.isPending}
        canDoubleDown={canDoubleDown}
        onGenerate={(blocks) => {
          const lead = doubleDownAd?.rows[0];
          if (!tenantId || !doubleDownAd || !lead) return;
          setPostFailure(null);
          doubleDown.mutate({
            tenantId,
            idempotencyKey: doubleDownIdempotencyKeyRef.current,
            hash: doubleDownAd.hash,
            windowDays: lead.window_days,
            source: lead.source,
            blocks,
          });
        }}
        onClose={() => {
          const reopenHash = returnToDrawerHash;
          setDoubleDownHash(null);
          setReturnToDrawerHash(null);
          if (reopenHash) onAdSelect?.(reopenHash);
        }}
      />
      </div>
    </div>
  );
}

interface InFlightDoubleDownRefs {
  inFlightKeysRef: MutableRefObject<Map<string, string>>;
  pollTokensRef: MutableRefObject<WeakMap<StartPostFlightDoubleDownRequest, number>>;
  latestPollTokenRef: MutableRefObject<Map<string, number>>;
  pollTokenSeqRef: MutableRefObject<number>;
}

function retainInFlightDoubleDown(
  variables: StartPostFlightDoubleDownRequest,
  refs: InFlightDoubleDownRefs,
): void {
  const token = refs.pollTokenSeqRef.current + 1;
  refs.pollTokenSeqRef.current = token;
  refs.pollTokensRef.current.set(variables, token);
  refs.latestPollTokenRef.current.set(variables.hash, token);
  refs.inFlightKeysRef.current.set(variables.hash, variables.idempotencyKey);
}

function releaseInFlightDoubleDown(
  variables: StartPostFlightDoubleDownRequest,
  refs: InFlightDoubleDownRefs,
): void {
  const token = refs.pollTokensRef.current.get(variables);
  if (token == null || refs.latestPollTokenRef.current.get(variables.hash) !== token) return;
  refs.inFlightKeysRef.current.delete(variables.hash);
  refs.latestPollTokenRef.current.delete(variables.hash);
}

/** The linked ad is included in the type filter once, so a later removal stays removed. */
function findLinkedCreativeKind(
  ads: ReturnType<typeof qualifyingAds>,
  adHash: string | undefined,
  isLoaded: boolean,
): PostFlightAdType | null {
  if (!isLoaded || !adHash) return null;
  const linked = ads.find((ad) => ad.hash === adHash);
  return linked ? creativeKind(linked) : null;
}
